import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Dumbbell, Brain, ChevronRight, Sparkles, Star, Zap, Apple, Users, Activity, Clock, Target, Heart, CheckCircle, Trophy, Smartphone, ArrowRight, Play, Phone, MessageCircle, BarChart3, Calendar } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const programs = [
  {
    name: 'Hypertrophy Training',
    description: 'Build muscle mass and improve body composition with scientifically-backed training methods',
    features: [
      'Progressive overload principles',
      'Volume optimization',
      'Exercise variation',
      'Recovery management'
    ],
    benefits: [
      'Increased muscle mass',
      'Better body composition',
      'Improved strength',
      'Enhanced aesthetics'
    ],
    icon: Dumbbell,
    color: 'from-blue-500/20 to-cyan-500/20',
    image: 'https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  },
  {
    name: 'Strength Training',
    description: 'Develop raw power and functional strength through structured programming',
    features: [
      'Compound movement focus',
      'Periodization cycles',
      'Technical mastery',
      'Performance tracking'
    ],
    benefits: [
      'Increased strength',
      'Better power output',
      'Improved technique',
      'Enhanced performance'
    ],
    icon: Trophy,
    color: 'from-cyan-500/20 to-teal-500/20',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  },
  {
    name: 'Functional Training',
    description: 'Enhance overall fitness and prepare for HYROX competitions with specialized workouts',
    features: [
      'Sport-specific training',
      'Endurance building',
      'Movement patterns',
      'Competition prep'
    ],
    benefits: [
      'Better endurance',
      'Functional strength',
      'Movement efficiency',
      'Competition readiness'
    ],
    icon: Zap,
    color: 'from-teal-500/20 to-emerald-500/20',
    image: 'https://images.unsplash.com/photo-1517963879433-6ad2b056d712?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  },
  {
    name: 'Nutrition Programming',
    description: 'Comprehensive nutrition plans for your specific goals - whether cutting, maintaining, or bulking',
    features: [
      'Macro calculations',
      'Meal planning',
      'Supplement guidance',
      'Progress monitoring'
    ],
    benefits: [
      'Optimized nutrition',
      'Better recovery',
      'Goal achievement',
      'Sustainable habits'
    ],
    icon: Apple,
    color: 'from-emerald-500/20 to-blue-500/20',
    image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  }
];

const appFeatures = [
  {
    icon: Target,
    title: 'Smart Goal Setting',
    description: 'Set and track your fitness goals with AI-powered recommendations',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Play,
    title: 'HD Video Library',
    description: 'Access 1000+ exercise technique videos',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    description: 'Track every aspect of your progress',
    color: 'from-teal-500/20 to-emerald-500/20'
  },
  {
    icon: Apple,
    title: 'Nutrition AI',
    description: 'Smart meal planning and tracking',
    color: 'from-emerald-500/20 to-blue-500/20'
  },
  {
    icon: MessageCircle,
    title: 'Coach Chat',
    description: 'Direct access to expert advice',
    color: 'from-blue-500/20 to-purple-500/20'
  },
  {
    icon: Calendar,
    title: 'Smart Scheduling',
    description: 'AI-powered workout planning',
    color: 'from-purple-500/20 to-pink-500/20'
  }
];

const testimonials = [
  {
    name: "Sarah M.",
    role: "Fitness Enthusiast",
    quote: "This app has completely transformed my training. The personalized programs and expert guidance have helped me achieve results I never thought possible.",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
  },
  {
    name: "Mike R.",
    role: "Amateur Athlete",
    quote: "The nutrition guidance and workout tracking features are incredible. It's like having a personal trainer in your pocket 24/7.",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
  },
  {
    name: "Emma L.",
    role: "HYROX Competitor",
    quote: "The functional training programs have taken my performance to the next level. I'm more prepared than ever for competitions.",
    image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
  }
];

export default function TrainingAppPage() {
  const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { scrollYProgress } = useScroll();
  const phoneY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);
  const phoneRotate = useTransform(scrollYProgress, [0, 0.5], [0, 10]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://i.ibb.co/k2XPcbyX/20250426-Fitness-Portal-Hyrox-34.jpg')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
            
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, delay: 1.5, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full blur-3xl"
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                  className="inline-block mb-6"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                    <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                        7 DAY FREE TRIAL
                      </span>
                      <Star className="w-5 h-5 text-yellow-400" />
                    </div>
                  </div>
                </motion.div>

                <h1 className="text-7xl font-bold mb-6">
                  YOUR
                  <div className="relative inline-block mx-4">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                      COACH
                    </span>
                  </div>
                  IN YOUR POCKET
                </h1>
                <p className="text-xl text-gray-400 mb-8">Expert guidance, personalized programs, and daily motivation - all in one app</p>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://kahunas.io/coach/packages/list/4fd06aef-0c5c-4810-bd0d-8e4235fe2c3f"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                  >
                    <span>START FREE TRIAL</span>
                    <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm px-6 py-3 rounded-full"
                  >
                    <Phone className="w-5 h-5 text-blue-400" />
                    <span className="text-gray-300">Available on iOS & Android</span>
                  </motion.div>
                </div>

                {/* App Store Badges */}
                <div className="flex flex-wrap gap-4 mt-8">
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 flex items-center space-x-3">
                    <Apple className="w-8 h-8" />
                    <div>
                      <div className="text-xs">Download on the</div>
                      <div className="text-lg font-bold">App Store</div>
                    </div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 flex items-center space-x-3">
                    <Play className="w-8 h-8" />
                    <div>
                      <div className="text-xs">GET IT ON</div>
                      <div className="text-lg font-bold">Google Play</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative hidden lg:block"
                style={{
                  y: phoneY,
                  rotate: phoneRotate
                }}
              >
                <div className="relative w-[300px] h-[600px] mx-auto">
                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-[3rem] transform rotate-6"
                  />
                  <motion.div
                    animate={{
                      y: [0, 10, 0],
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-[3rem] transform -rotate-6"
                  />
                  <div className="absolute inset-0 bg-black rounded-[3rem] border-4 border-white/10">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl" />
                    <div className="absolute inset-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-3xl overflow-hidden">
                      <motion.div
                        animate={{
                          y: [0, -300, 0],
                        }}
                        transition={{ duration: 20, repeat: Infinity }}
                        className="w-full h-[900px] bg-gradient-to-br from-blue-500/5 to-cyan-500/5"
                      >
                        {/* App UI Elements */}
                        <div className="p-4 space-y-4">
                          <div className="h-8 w-3/4 bg-white/10 rounded-lg" />
                          <div className="h-32 bg-white/5 rounded-xl" />
                          <div className="grid grid-cols-2 gap-4">
                            <div className="h-24 bg-white/5 rounded-lg" />
                            <div className="h-24 bg-white/5 rounded-lg" />
                          </div>
                          <div className="h-48 bg-white/5 rounded-xl" />
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* App Features Grid */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">APP FEATURES</h2>
              <p className="text-xl text-gray-400">Everything you need to succeed</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {appFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group cursor-pointer"
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <motion.div
                    animate={{
                      scale: hoveredFeature === index ? 1.05 : 1,
                      opacity: hoveredFeature === index ? 1 : 0.3,
                    }}
                    transition={{ duration: 0.3 }}
                    className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl blur-xl`}
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 h-full transform transition-transform duration-300">
                    <motion.div
                      animate={{
                        y: hoveredFeature === index ? -5 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <feature.icon className="w-12 h-12 text-blue-400 mb-6" />
                      <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                      <p className="text-gray-400">{feature.description}</p>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Programs Section */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">TRAINING PROGRAMS</h2>
              <p className="text-xl text-gray-400">Choose your path to success</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {programs.map((program, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedProgram(selectedProgram === index ? null : index)}
                >
                  <motion.div
                    animate={{
                      scale: selectedProgram === index ? 1.02 : 1,
                    }}
                    transition={{ duration: 0.3 }}
                    className="relative"
                  >
                    <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                      <div className="aspect-w-16 aspect-h-9">
                        <img
                          src={program.image}
                          alt={program.name}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
                      </div>
                      <div className="p-8">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className={`bg-gradient-to-br ${program.color} p-3 rounded-xl`}>
                            <program.icon className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold">{program.name}</h3>
                        </div>
                        
                        <p className="text-gray-300 mb-6">{program.description}</p>
                        
                        <AnimatePresence>
                          {selectedProgram === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-lg font-bold mb-4 text-blue-400">Features</h4>
                                  <div className="grid grid-cols-1 gap-3">
                                    {program.features.map((feature, i) => (
                                      <div
                                        key={i}
                                        className={`bg-gradient-to-r ${program.color} rounded-xl p-3 backdrop-blur-sm transform hover:scale-105 transition-transform duration-300`}
                                      >
                                        <span className="text-sm">{feature}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-lg font-bold mb-4 text-cyan-400">Benefits</h4>
                                  <div className="grid grid-cols-1 gap-3">
                                    {program.benefits.map((benefit, i) => (
                                      <div
                                        key={i}
                                        className={`bg-gradient-to-r ${program.color} rounded-xl p-3 backdrop-blur-sm transform hover:scale-105 transition-transform duration-300`}
                                      >
                                        <span className="text-sm">{benefit}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">USER STORIES</h2>
              <p className="text-xl text-gray-400">Hear from our community</p>
            </motion.div>

            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-1">
                      <div className="relative w-32 h-32 mx-auto rounded-full overflow-hidden">
                        <img
                          src={testimonials[currentTestimonial].image}
                          alt={testimonials[currentTestimonial].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xl mb-6">"{testimonials[currentTestimonial].quote}"</p>
                      <div>
                        <p className="font-bold text-lg">{testimonials[currentTestimonial].name}</p>
                        <p className="text-gray-400">{testimonials[currentTestimonial].role}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-white/10 text-center relative overflow-hidden"
            >
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 blur-3xl"
              />
              
              <div className="relative">
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                  className="inline-block mb-6"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                    <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                      <Sparkles className="w-5 h-5 text-yellow-400" />
                      <span className="text-white font-bold">START YOUR FREE 7-DAY TRIAL</span>
                      <Sparkles className="w-5 h-5 text-yellow-400" />
                    </div>
                  </div>
                </motion.div>

                <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Training?</h2>
                <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                  Experience the power of personalized programming with expert guidance. No commitment required - try it free for 7 days.
                </p>

                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="https://kahunas.io/coach/packages/list/4fd06aef-0c5c-4810-bd0d-8e4235fe2c3f"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 group"
                  >
                    <span>GET STARTED NOW</span>
                    <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm px-6 py-4 rounded-full"
                  >
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span className="text-gray-300">No Credit Card Required</span>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}